import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, ArrowRight } from 'lucide-react';
import SEO from '../../../components/SEO';
import LandingNavBar from '../../../components/LandingNavBar';
import LandingFooter from '../../../components/LandingFooter';
import blogArticles, { formatBlogDate } from './blogArticles';

const Reveal = ({ children, delay = 0 }) => (
  <div className="animate-fade-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}>
    {children}
  </div>
);

const categoryColors = {
  'Compliance': 'bg-red-500/10 text-red-400 border-red-500/20',
  'Setup Guide': 'bg-green-500/10 text-green-400 border-green-500/20',
  'Integration': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Technical': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'HR Guide': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const renderContent = (text) => {
  // Support: **bold**, `code`, [text](url), and \n line breaks
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\n)/g);
  return parts.map((part, i) => {
    if (part === '\n') return <br key={i} />;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-slate-900 font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-sm font-mono">{part.slice(1, -1)}</code>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, url] = linkMatch;
      const isExternal = /^https?:\/\//.test(url);
      if (isExternal) {
        return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-600 underline underline-offset-2 font-medium">{label}</a>;
      }
      return <Link key={i} to={url} className="text-emerald-700 hover:text-emerald-600 underline underline-offset-2 font-medium">{label}</Link>;
    }
    return <span key={i}>{part}</span>;
  });
};

const BlogArticle = () => {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const relatedArticles = (article.relatedSlugs || [])
    .map(s => blogArticles.find(a => a.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      <LandingNavBar />
      <SEO
        title={`${article.title} | StayMii Blog`}
        description={article.description}
        keywords={article.keywords}
        canonicalUrl={`/blog/${article.slug}`}
      />

      {/* Article Header */}
      <section className="pt-28 pb-12 bg-[#0B1121]" data-testid="article-header">
        <div className="max-w-3xl mx-auto px-4">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-8 transition-colors" data-testid="back-to-blog">
              <ArrowLeft className="h-4 w-4" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryColors[article.category]}`}>
                {article.category}
              </span>
              <span className="text-white/40 text-sm flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {article.readTime}
              </span>
              <span className="text-white/30 text-sm">{formatBlogDate(article.date)}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-white/50 text-base lg:text-lg max-w-2xl">
              {article.description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="bg-slate-50 border-b border-slate-200" data-testid="article-toc">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">In this article</p>
          <nav className="flex flex-wrap gap-2">
            {article.sections.map((section, i) => (
              <a
                key={i}
                href={`#section-${i}`}
                className="text-sm text-slate-600 hover:text-[#2563EB] bg-white border border-slate-200 hover:border-blue-200 rounded-lg px-3 py-1.5 transition-colors"
                data-testid={`toc-link-${i}`}
              >
                {section.heading}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 px-4" data-testid="article-content">
        <div className="max-w-3xl mx-auto">
          {article.sections.map((section, i) => (
            <Reveal key={i} delay={i * 60}>
              <section id={`section-${i}`} className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">
                  {section.heading}
                </h2>
                <div className="text-slate-600 leading-relaxed text-base space-y-1">
                  {renderContent(section.content)}
                </div>
              </section>
            </Reveal>
          ))}
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 px-4 bg-slate-50 border-t border-slate-100" data-testid="related-articles">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="block bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-md transition-all group"
                  data-testid={`related-${related.slug}`}
                >
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium border mb-3 ${categoryColors[related.category]}`}>
                    {related.category}
                  </span>
                  <h3 className="font-semibold text-slate-900 group-hover:text-[#2563EB] transition-colors mb-2 leading-snug">
                    {related.title}
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-2 mb-3">{related.description}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium group-hover:gap-2.5 transition-all">
                    Read <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 bg-[#0B1121]" data-testid="article-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Automate Your Hotel e-Invoice Compliance
          </h2>
          <p className="text-white/50 mb-8 text-base">
            Join Malaysian hotels using StayMii. Setup takes less than 30 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="px-8 py-3.5 bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-xl font-medium transition-colors" data-testid="article-cta-start">
              Get Started Free
            </Link>
            <Link to="/features" className="px-8 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-medium transition-colors" data-testid="article-cta-features">
              Explore Features
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default BlogArticle;
